import { FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';

import { useStyles } from './styles';

export interface Segment<T extends string> {
  value: T;
  label: string;
}

interface SegmentedChoiceProps<T extends string> {
  /**
   * Accessible name of the group. A radio group has no control to hang a label
   * on, so one of this and `labelledBy` is always needed.
   *
   * Ignored when `labelledBy` names an element on screen — an `aria-label`
   * beside a visible label is a second wording free to drift from the first,
   * and a screen reader would read the pair twice.
   */
  label?: string;
  /** Id of the visible element naming the group, when there is one. */
  labelledBy?: string;
  value: T;
  options: Array<Segment<T>>;
  onChange: (next: T) => void;
  /**
   * The compact size, for a segmented control sitting inside a card rather than
   * in a page toolbar.
   */
  dense?: boolean;
  className?: string;
}

/**
 * One choice from a short, mutually exclusive set, drawn as joined segments.
 *
 * A radio group wearing segments, not a pair of toggle buttons — the standing
 * decision from AXHE-4465, and it holds wherever this is used:
 *
 *   The control always shows one of its set and never none, which is the radio
 *     contract exactly. Toggle buttons carry `aria-pressed` and permit "all
 *     off", so an exclusive group has to *guard* against a re-click
 *     deselecting the only choice. Here that state cannot be expressed.
 *   A radio group is one tab stop with the arrow keys moving inside it. A pair
 *     of buttons is two tab stops with no arrow keys.
 *   The ARIA APG maps mutually exclusive buttons with one always pressed to
 *     `radiogroup`/`radio` rather than to `aria-pressed`.
 *
 * The radios themselves are visually hidden rather than `display: none`, which
 * would take the inputs out of the accessibility tree along with the glyphs and
 * leave a group no keyboard can reach. Each label is the visible segment.
 *
 * Generic over the value so a caller's union survives `onChange` — the panel
 * writes `'any' | 'all'` straight onto a draft field of that type.
 */
export const SegmentedChoice = <T extends string>({
  label,
  labelledBy,
  value,
  options,
  onChange,
  dense = false,
  className,
}: SegmentedChoiceProps<T>): React.ReactElement => {
  const styles = useStyles();

  // No `name` on the group: `RadioGroup` mints a unique one per instance, and a
  // hardcoded one would make every instance on a page a single *native* radio
  // group — the browser groups radios by name, so checking a side in one would
  // uncheck it in the others and leave React's controlled value arguing with the
  // DOM. That is not hypothetical here: the panel renders one of these per
  // level.
  return (
    <RadioGroup
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      className={classNames(styles.segmentGroup, className)}
      row
      value={value}
      onChange={event => onChange(event.target.value as T)}
    >
      {options.map(option => (
        <FormControlLabel
          className={classNames(styles.segment, {
            [styles.segmentDense]: dense,
            [styles.segmentSelected]: option.value === value,
          })}
          // `disableRipple` because the ripple draws on the hidden control, so
          // it would animate inside a 1px box the segment does not contain.
          control={<Radio className={styles.segmentRadio} disableRipple />}
          key={option.value}
          label={option.label}
          value={option.value}
        />
      ))}
    </RadioGroup>
  );
};
